import React, { useState } from 'react';
import { speakersService } from '../../services/speakers';
import { notificationService } from '../../services/notifications';
import { getSpeakerColor } from '../../utils/speakerColors';
import { Speaker } from '../../types';
import { UserIcon, DeleteIcon } from '../../icons';
import { CONTAINER_STYLES } from '../../styles/containers';
import EditButtons from '../ui/EditButtons';
import StatCard from '../ui/StatCard';

interface SpeakerSectionProps {
  speaker: Speaker;
  audioFileId: string;
  onSpeakerUpdate: (updatedSpeaker: Speaker) => void;
  onSpeakerUpdated?: () => void;
  onSpeakerDelete?: (speakerId: string) => void;
  speakers: Speaker[];
}

const SpeakerSection: React.FC<SpeakerSectionProps> = ({
  speaker,
  audioFileId,
  onSpeakerUpdate,
  onSpeakerUpdated,
  onSpeakerDelete,
  speakers,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(speaker.name || '');
  const [editedRole, setEditedRole] = useState(speaker.role || '');
  const [isLoading, setIsLoading] = useState(false);

  const handleEditStart = () => {
    setEditedName(speaker.name || '');
    setEditedRole(speaker.role || '');
    setIsEditing(true);
  };

  const handleEditCancel = () => {
    setEditedName(speaker.name || '');
    setEditedRole(speaker.role || '');
    setIsEditing(false);
  };

  const handleEditSave = async () => {
    if (editedName.trim() === '') return;

    setIsLoading(true);
    try {
      const updatedSpeaker = await speakersService.updateSpeaker(
        audioFileId,
        speaker.id,
        {
          name: editedName.trim(),
          role: editedRole.trim() || undefined,
        }
      );
      onSpeakerUpdate(updatedSpeaker);
      onSpeakerUpdated?.();
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update speaker:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleEditSave();
    } else if (e.key === 'Escape') {
      handleEditCancel();
    }
  };

  const handleDelete = async () => {
    if (speaker.utteranceCount > 0) {
      notificationService.error(
        'Cannot delete speaker: speaker has utterances'
      );
      return;
    }

    setIsLoading(true);
    try {
      await speakersService.deleteSpeaker(audioFileId, speaker.id);
      onSpeakerDelete?.(speaker.id);
      onSpeakerUpdated?.();
      notificationService.success(
        `Speaker "${speaker.originalLabel}" deleted successfully`
      );
    } catch (error) {
      console.error('Failed to delete speaker:', error);
      notificationService.error('Failed to delete speaker. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const hasNoUtterances = speaker.utteranceCount === 0;

  return isEditing ? (
    <div className={CONTAINER_STYLES.editMode}>
      <div className="flex items-center gap-2 mb-3">
        <UserIcon
          size={16}
          color={getSpeakerColor(speaker.id, speakers)}
          filled
        />
        <span className="font-medium text-gray-900 dark:text-gray-100">
          {speaker.originalLabel}
        </span>
      </div>
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Name
          </label>
          <input
            type="text"
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            onKeyDown={handleKeyDown}
            className={CONTAINER_STYLES.input}
            placeholder="Speaker name"
            autoFocus
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Role
          </label>
          <input
            type="text"
            value={editedRole}
            onChange={(e) => setEditedRole(e.target.value)}
            onKeyDown={handleKeyDown}
            className={CONTAINER_STYLES.input}
            placeholder="Speaker role (optional)"
          />
        </div>
        <EditButtons
          onSave={handleEditSave}
          onCancel={handleEditCancel}
          isLoading={isLoading}
          saveDisabled={editedName.trim() === ''}
        />
      </div>
    </div>
  ) : (
    <div
      className={`${CONTAINER_STYLES.showMode} group cursor-pointer`}
      onClick={handleEditStart}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <UserIcon
            size={16}
            color={getSpeakerColor(speaker.id, speakers)}
            filled
          />
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {speaker.originalLabel}
          </span>
        </div>
        {hasNoUtterances && (
          <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDelete();
              }}
              disabled={isLoading}
              className="p-1 text-gray-400 hover:text-red-400 dark:text-gray-500 dark:hover:text-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Delete speaker"
            >
              <DeleteIcon size={16} />
            </button>
          </div>
        )}
      </div>
      <div className="space-y-2">
        <StatCard
          variant="row"
          label="Name"
          value={speaker.name || 'Not specified'}
        />
        <StatCard
          variant="row"
          label="Role"
          value={speaker.role || 'Not specified'}
        />
        <StatCard
          variant="row"
          label="Utterances"
          value={speaker.utteranceCount}
        />
        <StatCard
          variant="row"
          label="Speaking time"
          value={`${speaker.speakingPercentage}%`}
        />
      </div>
    </div>
  );
};

export default SpeakerSection;
